import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AuthService } from './auth/auth.service';

// Import child components
import { NavigationComponent } from './navigation/navigation.component';
import { HeaderComponent } from './header/header.component';
import { TopHeaderComponent } from './top-header/top-header.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    TranslateModule,
    NavigationComponent,
    HeaderComponent,
    TopHeaderComponent
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit, AfterViewInit, OnDestroy {

  title = 'cDoc';

  currentUser = signal<any>(null);
  isAuthenticated: boolean = false; // Start with false
  toggleNav: boolean = false;
  showMobileMenu: boolean = false;

  constructor(private authService: AuthService, private translate: TranslateService, private cdr: ChangeDetectorRef) {
    // Check authentication status
    this.isAuthenticated = this.authService.getIsAuth();

    translate.addLangs(['en', 'sv', 'fi']);
    translate.setDefaultLang('sv');

    const usedLanguage = localStorage.getItem('usedLanguage');
    translate.use(usedLanguage ?? 'sv');

    if (!usedLanguage) {
      localStorage.setItem('usedLanguage', 'sv');
    }
  }

  ngOnInit(): void {
    // Check authentication status again in case it changed
    this.isAuthenticated = this.authService.getIsAuth();
    
    this.toggleNav = localStorage.getItem('toggleNav') === 'true';

    const data = localStorage.getItem('currentUser');
    if (data) {
      this.currentUser.set(JSON.parse(data));

      if (this.currentUser()?.is_guest === 1) {
        this.toggleNav = true;
      }
    }
  }

  private scrollObserver?: MutationObserver;

  ngAfterViewInit(): void {
    // Check for inventory status and apply CSS class
    setTimeout(() => {
      this.updateHeaderClass();
    }, 100);

    // Ensure body scrolling is always enabled
    this.ensureBodyScrolling();
    
    // Monitor for any changes that might disable scrolling using MutationObserver
    this.setupScrollProtection();
  }

  ngOnDestroy(): void {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect();
    }
  }

  setupScrollProtection(): void {
    const body = document.body;
    const html = document.documentElement;
    
    // Create observer to watch for style changes on body and html
    this.scrollObserver = new MutationObserver(() => {
      this.ensureBodyScrolling();
    });
    
    // Observe style attribute changes on body and html
    this.scrollObserver.observe(body, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    this.scrollObserver.observe(html, {
      attributes: true,
      attributeFilter: ['style']
    });
    
    // Also check periodically as a fallback
    setInterval(() => {
      this.ensureBodyScrolling();
    }, 2000);
  }

  ensureBodyScrolling(): void {
    const body = document.body;
    const html = document.documentElement;
    
    // Force enable scrolling on body
    if (body.style.overflow === 'hidden' || body.style.overflowY === 'hidden') {
      body.style.overflow = '';
      body.style.overflowY = 'auto';
    }
    
    // Force enable scrolling on html
    if (html.style.overflow === 'hidden' || html.style.overflowY === 'hidden') {
      html.style.overflow = '';
      html.style.overflowY = 'auto';
    }
    
    // Also check computed styles as a fallback
    const bodyComputed = window.getComputedStyle(body);
    const htmlComputed = window.getComputedStyle(html);
    
    if (bodyComputed.overflow === 'hidden' || bodyComputed.overflowY === 'hidden') {
      body.style.setProperty('overflow', 'auto', 'important');
      body.style.setProperty('overflow-y', 'auto', 'important');
    }
    
    if (htmlComputed.overflow === 'hidden' || htmlComputed.overflowY === 'hidden') {
      html.style.setProperty('overflow', 'auto', 'important');
      html.style.setProperty('overflow-y', 'auto', 'important');
    }
  }

  // Add mobile menu methods
  openMobileMenu(): void {
    this.showMobileMenu = true;
  }

  closeMobileMenu(): void {
    this.showMobileMenu = false;
  }

  toggleNavigation(): void {
    if (this.currentUser()?.is_guest === 1) return;

    this.toggleNav = !this.toggleNav;
    localStorage.setItem('toggleNav', this.toggleNav ? 'true' : 'false');
  }

  updateHeaderClass(): void {
    // Check if inventory status label is visible
    const inventoryLabel = document.querySelector('.bg-red-500.rounded') as HTMLElement;
    const body = document.body;
    
    // Check if label exists and has content
    const hasContent = inventoryLabel && (
      inventoryLabel.offsetHeight > 0 || 
      inventoryLabel.textContent?.trim() !== '' ||
      inventoryLabel.offsetWidth > 0
    );
    
    if (hasContent) {
      body.classList.add('inventory-ongoing');
      // Set CSS custom property for increased padding
      document.documentElement.style.setProperty('--main-padding-top', '140px');
    } else {
      body.classList.remove('inventory-ongoing');
      // Reset CSS custom property to default
      document.documentElement.style.setProperty('--main-padding-top', '112px');
    }
  }

  // Method to recalculate padding when header content changes
  recalculatePadding(): void {
    setTimeout(() => {
      this.updateHeaderClass();
    }, 200);
  }
}
